import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useAppContext } from "./AppContext";
import { toNumber } from "lodash";
import AppflyteTransactionApi from "../Api/Services/AppflyteBackend/AppflyteTransactionApi";
import { getPlanDetails } from "../utils/ApiFunctions/AppflyteTransactions";

const CreditContext = createContext();

export const CreditProvider = ({ children }) => {
    const { selectedOrganization } = useAppContext();
    const unsubscribeRef = useRef(null);

    const [creditLoading, setCreditLoading] = useState(true);
    const [warningStatus, setWarningStatus] = useState('')
    const [warningMessage, setWarningMessage] = useState('')

    const [planDetails, setPlanDetails] = useState(null)
    const planDetailsRef = useRef(null);
    const [planType, setPlanType] = useState(null);
    const [apiBalance, setApiBalance] = useState(0);
    const [projectUsed, setProjectUsed] = useState(0);
    const [projectBalance, setProjectBalance] = useState(0);
    const [subscriptionEnd, setSubscriptionEnd] = useState(null);
    const [creditCycleEnd, setCreditCycleEnd] = useState(null);

    const formatCompact = (value) => {
        return new Intl.NumberFormat('en', {
            notation: 'compact',
            maximumFractionDigits: 1,
        }).format(value);
    };

    const handleWarningMessage = (percentageUsed, remaining) => {
        if (percentageUsed >= 100) {
            setWarningMessage("You've used all your API calls for this plan. Upgrade your plan to continue.");
            setWarningStatus(true);
        }
        else if (percentageUsed >= 90) {
            setWarningMessage(`${formatCompact(Number(remaining || 0))} API calls remaining in this plan. Consider upgrading to avoid interruptions.`);
            setWarningStatus(true);
        }
        else if (percentageUsed >= 70) {
            setWarningMessage(`${formatCompact(Number(remaining || 0))} API calls remaining in this plan.`);
            setWarningStatus(true);
        }
        else {
            setWarningStatus(false);
            setWarningMessage("");
        }
    };

    const isExpiringInOneDay = (subscription_end) => {
        if (!subscription_end) return false;

        const endDate = new Date(subscription_end * 1000);
        const now = new Date();

        const diffMs = endDate - now;
        const oneDayMs = 24 * 60 * 60 * 1000;

        return diffMs > 0 && diffMs <= oneDayMs;
    };

    useEffect(() => {
        fetchCreditBalance()
    }, [selectedOrganization])

    const fetchCreditBalance = async () => {
        try {
            setCreditLoading(true);
            const orgId = selectedOrganization?.payload?.__auto_id__;
            if (orgId) {

                const responseData = await getPlanDetails(orgId)
                if (responseData) {

                    setPlanDetails(responseData)
                    planDetailsRef.current = responseData;

                    const plan_type = responseData?.plan_type ?? null;
                    const subscription_end = responseData?.subscription_end ?? null;
                    const credit_cycle_end = responseData?.credit_cycle_end ?? null;
                    setPlanType(plan_type);
                    setSubscriptionEnd(subscription_end);
                    setCreditCycleEnd(credit_cycle_end);

                    const project_used = toNumber(responseData?.projects_used ?? 0);
                    const project_limit = toNumber(responseData?.features?.projects ?? 0);
                    const project_balance = project_limit - project_used
                    setProjectBalance(project_balance)
                    setProjectUsed(project_used)

                    const api_calls_used = toNumber(responseData?.api_calls_used ?? 0);
                    const api_limit = toNumber(responseData?.features?.api_calls ?? 0);
                    const api_balance = api_limit - api_calls_used;
                    setApiBalance(api_balance)
                }
            }
        } catch (error) {
            console.log(error)
        } finally {
            setCreditLoading(false);
        }
    }

    useEffect(() => {

        const orgId = selectedOrganization?.payload?.__auto_id__;
        if (!orgId) return;

        const baseUrl = window.__ENV__.APPFLYTE_AUTHENTICATION_URL;
        const wsUrl = baseUrl.replace("http://", "ws://").replace("https://", "wss://");
        const ws = new WebSocket(`${wsUrl}/ws/organization/${orgId}`);

        ws.onerror = (e) => console.log("[WS] ERROR", e);
        ws.onclose = (e) => console.log("[WS] CLOSE", e.code, e.reason);

        ws.onmessage = (event) => {
            const data = JSON.parse(event.data);

            if (data.type === "api_call_increment") {
                const organization_id = data?.organization_id ?? null;
                if (organization_id === orgId) {
                    const api_calls_used = toNumber(data.api_calls_used ?? 0)
                    const api_limit = toNumber(planDetailsRef.current?.features?.api_calls ?? 0);
                    const api_balance = api_limit - api_calls_used;
                    setApiBalance(api_balance)
                }
            }

            if (data.type === "project_increment") {
                const organization_id = data?.organization_id ?? null;
                if (organization_id === orgId) {
                    const project_used = toNumber(data.project_used ?? 0)
                    const project_limit = toNumber(planDetailsRef.current?.features?.projects ?? 0);
                    const project_balance = project_limit - project_used
                    setProjectBalance(project_balance)
                    setProjectUsed(project_used)
                }
            }

            if (data.type === "plan_update") {
                const organization_id = data?.organization_id ?? null;
                if (organization_id === orgId) {

                    const plan_type = data?.plan_type ?? null;
                    const subscription_end = data?.subscription_end ?? null;
                    const credit_cycle_end = data?.credit_cycle_end ?? null;
                    setPlanType(plan_type);
                    setSubscriptionEnd(subscription_end);
                    setCreditCycleEnd(credit_cycle_end);

                    const project_limit = toNumber(data?.project_limit ?? 0);
                    const project_used = toNumber(data?.project_used ?? 0)
                    const project_balance = project_limit - project_used
                    setProjectUsed(project_used)
                    setProjectBalance(project_balance)

                    const api_limit = toNumber(data?.api_limit ?? 0);
                    const api_calls_used = toNumber(data?.api_calls_used ?? 0);
                    const api_balance = api_limit - api_calls_used;
                    setApiBalance(api_balance)
                }
            }

            if (data.type === "plan_downgrade") {
                const organization_id = data?.organization_id ?? null;
                if (organization_id === orgId) {

                    const plan_type = data?.plan_type ?? null;
                    const subscription_end = data?.subscription_end ?? null;
                    const credit_cycle_end = data?.credit_cycle_end ?? null;
                    setPlanType(plan_type);
                    setSubscriptionEnd(subscription_end);
                    setCreditCycleEnd(credit_cycle_end);

                    const project_limit = toNumber(data?.project_limit ?? 0);
                    const project_used = toNumber(data?.project_used ?? 0)
                    const project_balance = project_limit - project_used
                    setProjectUsed(project_used)
                    setProjectBalance(project_balance)

                    const api_limit = toNumber(data?.api_limit ?? 0);
                    const api_calls_used = toNumber(data?.api_calls_used ?? 0);
                    const api_balance = api_limit - api_calls_used;
                    setApiBalance(api_balance)
                }
            }

        };

        return () => {
            ws.close();
        };

    }, [selectedOrganization]);

    const shouldShowCreditWarning = () => {
        if (!selectedOrganization || creditLoading || !warningStatus) return false;
        return warningStatus
    };

    const isProExpired = planType === "pro" && subscriptionEnd ? (subscriptionEnd * 1000) < Date.now() : false;

    return (
        <CreditContext.Provider value={{
            apiBalance,
            projectBalance,
            warningMessage,
            shouldShowCreditWarning,
            planType,
            subscriptionEnd,
            creditCycleEnd,
            projectUsed,
            isProExpired,
            creditLoading
        }}>
            {children}
        </CreditContext.Provider>
    );
};

export const useCredit = () => useContext(CreditContext);

const DEFAULTS_CONFIGS = {
    apiBalance: Infinity,
    projectBalance: Infinity,
    warningMessage: '',
    shouldShowCreditWarning: () => false,
    planType: 'free',
    subscriptionEnd: null,
    creditCycleEnd: null,
    projectUsed: 0,
    isProExpired: false,
    creditLoading: false,
};

export const NullCreditProvider = ({ children }) => (
    <CreditContext.Provider value={DEFAULTS_CONFIGS}>
        {children}
    </CreditContext.Provider>
);